import { useEffect, useMemo, useState } from 'react';
import { Loader2, Send, Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge, type BadgeTone } from '@/components/ui/StatusBadge';
import {
  useReviewRequests,
  useRunReviewPass,
  useSaveReviewSettings,
  useSendReview,
  type ReviewPassResult,
  type ReviewRequestRow,
  type ReviewSettings,
} from '@/hooks/admin/useReviewRequests';

/**
 * /admin/reviews — "how did we do?" emails after a Service Fusion service or
 * combo job closes. Settings, a send-one-by-hand form, a dry run of the hourly
 * pass, and every request with its rating.
 */

const STATUS_TONE: Record<ReviewRequestRow['status'], BadgeTone> = {
  rated: 'success',
  sent: 'info',
  claimed: 'neutral',
  skipped: 'neutral',
  failed: 'danger',
};

function fmt(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

function stars(n: number): string {
  return `${'★'.repeat(n)}${'☆'.repeat(5 - n)}`;
}

export default function AdminReviews() {
  const q = useReviewRequests();
  const save = useSaveReviewSettings();
  const send = useSendReview();
  const run = useRunReviewPass();

  const [draft, setDraft] = useState<ReviewSettings | null>(null);
  useEffect(() => {
    if (q.data?.settings && !draft) setDraft(q.data.settings);
  }, [q.data, draft]);

  const [job, setJob] = useState('');
  const [to, setTo] = useState('');
  const [resend, setResend] = useState(false);
  const [sendMsg, setSendMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [passResult, setPassResult] = useState<ReviewPassResult | null>(null);
  const [runErr, setRunErr] = useState<string | null>(null);

  const settings = q.data?.settings;
  const totals = q.data?.totals;

  async function toggleEnabled() {
    if (!settings) return;
    const turningOn = !settings.enabled;
    if (
      turningOn &&
      !window.confirm(
        'Turn on review emails? From now on, every Service Fusion service or combo job that closes gets a "how did we do?" email. Jobs that closed before now are not emailed.',
      )
    )
      return;
    const r = await save.mutateAsync({ enabled: turningOn });
    setDraft(r.settings);
  }

  async function saveDetails(e: React.FormEvent) {
    e.preventDefault();
    if (!draft) return;
    const r = await save.mutateAsync({
      low_score_threshold: draft.low_score_threshold,
      throttle_days: draft.throttle_days,
      links: draft.links,
    });
    setDraft(r.settings);
  }

  async function sendOne(e: React.FormEvent) {
    e.preventDefault();
    setSendMsg(null);
    try {
      const r = await send.mutateAsync({ job: job.trim(), to: to.trim() || undefined, resend });
      setSendMsg({ ok: true, text: `Sent to ${r.to.join(', ')}.` });
      setJob('');
      setTo('');
      setResend(false);
    } catch (err) {
      setSendMsg({ ok: false, text: err instanceof Error ? err.message : String(err) });
    }
  }

  async function runPass(dryRun: boolean) {
    setRunErr(null);
    setPassResult(null);
    if (!dryRun && !window.confirm('Run the pass for real now? Emails go to customers.')) return;
    try {
      setPassResult(await run.mutateAsync(dryRun));
    } catch (err) {
      setRunErr(err instanceof Error ? err.message : String(err));
    }
  }

  const columns = useMemo<Column<ReviewRequestRow>[]>(
    () => [
      {
        key: 'created',
        header: 'When',
        cell: (r) => fmt(r.sent_at ?? r.created_at),
        sortValue: (r) => r.sent_at ?? r.created_at,
      },
      {
        key: 'customer',
        header: 'Customer',
        cell: (r) => (
          <div>
            <div className="font-medium text-brix-ink">{r.customer_name ?? '—'}</div>
            {r.location_name && r.location_name !== r.customer_name && (
              <div className="text-xs text-brix-muted">{r.location_name}</div>
            )}
          </div>
        ),
        sortValue: (r) => r.customer_name ?? '',
      },
      {
        key: 'job',
        header: 'SF job',
        cell: (r) => (
          <div>
            <div className="tabular-nums">{r.sf_job_number ?? r.sf_job_id}</div>
            <div className="text-xs text-brix-muted">{r.job_status}</div>
          </div>
        ),
        sortValue: (r) => r.sf_job_number ?? r.sf_job_id,
        hideOnMobile: true,
      },
      {
        key: 'to',
        header: 'Sent to',
        cell: (r) => (
          <div className="text-xs text-brix-muted">
            {(r.recipients ?? []).join(', ') || '—'}
            <div>{r.source === 'manual' ? `by hand${r.sent_by ? ` · ${r.sent_by}` : ''}` : 'automatic'}</div>
          </div>
        ),
        hideOnMobile: true,
      },
      {
        key: 'status',
        header: 'Status',
        cell: (r) => (
          <div>
            <StatusBadge tone={STATUS_TONE[r.status]}>{r.status}</StatusBadge>
            {r.skip_reason && <div className="mt-1 text-xs text-brix-muted">{r.skip_reason}</div>}
            {r.error && <div className="mt-1 text-xs text-red-700">{r.error}</div>}
          </div>
        ),
        sortValue: (r) => r.status,
      },
      {
        key: 'rating',
        header: 'Rating',
        cell: (r) =>
          r.rating ? (
            <div>
              <span
                className={
                  settings && r.rating <= settings.low_score_threshold ? 'text-red-600' : 'text-amber-500'
                }
              >
                {stars(r.rating)}
              </span>
              {r.comment && <div className="mt-1 max-w-xs whitespace-pre-wrap text-xs text-brix-ink">{r.comment}</div>}
            </div>
          ) : (
            <span className="text-brix-muted">—</span>
          ),
        sortValue: (r) => r.rating ?? 0,
      },
    ],
    [settings],
  );

  return (
    <div className="px-6 py-6 space-y-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-brix-ink flex items-center gap-2">
          <Star size={16} className="text-brix-primary" />
          Reviews
        </h2>
        <p className="text-sm text-brix-muted mt-0.5 max-w-2xl">
          A "how did we do?" email goes out when a Service Fusion service or combo job is closed.
          Every rater is shown the Google, Yelp and Facebook links. A low score emails the service
          team.
        </p>
      </div>

      {q.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-brix-muted" />
      ) : q.error ? (
        <p className="text-sm text-red-700">{(q.error as Error).message}</p>
      ) : (
        settings &&
        draft && (
          <>
            {totals && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Tile label="Sent" value={String(totals.sent)} />
                <Tile label="Rated" value={String(totals.rated)} />
                <Tile label="Average" value={totals.average == null ? '—' : `${totals.average} ★`} />
                <Tile label="Low scores" value={String(totals.low)} />
              </div>
            )}

            <section className="rounded-xl border border-brix-border bg-brix-surface p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-brix-ink">
                    Automatic emails: {settings.enabled ? 'ON' : 'OFF'}
                  </div>
                  <div className="text-xs text-brix-muted">
                    {settings.enabled
                      ? `On since ${fmt(settings.enabled_at)}. Checked every hour.`
                      : 'Nothing is emailed automatically. You can still send one by hand below.'}
                  </div>
                </div>
                <Button variant={settings.enabled ? 'secondary' : 'primary'} size="sm" onClick={toggleEnabled} disabled={save.isPending}>
                  {settings.enabled ? 'Turn off' : 'Turn on'}
                </Button>
              </div>

              <form onSubmit={saveDetails} className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="rv-threshold">Alert the service team at or below</Label>
                  <select
                    id="rv-threshold"
                    value={draft.low_score_threshold}
                    onChange={(e) => setDraft({ ...draft, low_score_threshold: Number(e.target.value) })}
                    className="mt-1 h-10 w-full rounded-lg border border-brix-border bg-brix-surface px-3 text-sm"
                  >
                    {[1, 2, 3, 4].map((n) => (
                      <option key={n} value={n}>
                        {n} star{n > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="rv-throttle">At most one email per customer every (days)</Label>
                  <Input
                    id="rv-throttle"
                    type="number"
                    min={0}
                    max={365}
                    value={draft.throttle_days}
                    onChange={(e) => setDraft({ ...draft, throttle_days: Number(e.target.value) })}
                    className="mt-1"
                  />
                </div>
                {(['google', 'yelp', 'facebook'] as const).map((k) => (
                  <div key={k} className="sm:col-span-2">
                    <Label htmlFor={`rv-${k}`} className="capitalize">
                      {k} review link
                    </Label>
                    <Input
                      id={`rv-${k}`}
                      value={draft.links[k]}
                      onChange={(e) => setDraft({ ...draft, links: { ...draft.links, [k]: e.target.value } })}
                      placeholder="https://…"
                      className="mt-1"
                    />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <Button type="submit" size="sm" disabled={save.isPending}>
                    {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                  {save.error && <span className="ml-3 text-sm text-red-700">{(save.error as Error).message}</span>}
                </div>
              </form>
            </section>

            <section className="rounded-xl border border-brix-border bg-brix-surface p-4">
              <div className="mb-3 font-semibold text-brix-ink">Send one by hand</div>
              <form onSubmit={sendOne} className="grid gap-3 sm:grid-cols-[1fr_1.5fr_auto] sm:items-end">
                <div>
                  <Label htmlFor="rv-job">Service Fusion job #</Label>
                  <Input id="rv-job" value={job} onChange={(e) => setJob(e.target.value)} inputMode="numeric" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="rv-to">Email (optional — blank uses the one on file)</Label>
                  <Input id="rv-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1" />
                </div>
                <Button type="submit" disabled={send.isPending || !job.trim()}>
                  {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send size={14} className="mr-1.5" />Send</>}
                </Button>
              </form>
              <label className="mt-2 flex items-center gap-2 text-sm text-brix-muted">
                <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} />
                Send again if this job already got one
              </label>
              {sendMsg && <p className={`mt-2 text-sm ${sendMsg.ok ? 'text-emerald-700' : 'text-red-700'}`}>{sendMsg.text}</p>}
            </section>

            <section className="rounded-xl border border-brix-border bg-brix-surface p-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="mr-auto font-semibold text-brix-ink">Check what the hourly run would send</div>
                <Button variant="secondary" size="sm" onClick={() => runPass(true)} disabled={run.isPending}>
                  Dry run
                </Button>
                {settings.enabled && (
                  <Button variant="secondary" size="sm" onClick={() => runPass(false)} disabled={run.isPending}>
                    Run now
                  </Button>
                )}
              </div>
              {run.isPending && <Loader2 className="mt-3 h-4 w-4 animate-spin text-brix-muted" />}
              {runErr && <p className="mt-2 text-sm text-red-700">{runErr}</p>}
              {passResult && (
                <div className="mt-3 space-y-2 text-sm">
                  <p className="text-brix-muted">
                    {passResult.paused
                      ? 'Communications are paused — nothing sent.'
                      : passResult.dry_run
                        ? `Would send ${passResult.would_send.length}. Checked ${passResult.seen} jobs since ${fmt(passResult.cutoff)}.`
                        : `Sent ${passResult.sent}, failed ${passResult.failed}.`}
                    {passResult.budget_hit && ' Stopped early to stay within time; the rest go next hour.'}
                  </p>
                  {passResult.would_send.length > 0 && (
                    <ul className="list-disc pl-5 text-brix-ink">
                      {passResult.would_send.map((w) => (
                        <li key={w.job}>
                          Job {w.job} · {w.customer ?? '—'} → {w.to.join(', ')} <span className="text-brix-muted">({w.via})</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {passResult.skipped.length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-brix-muted">{passResult.skipped.length} skipped</summary>
                      <ul className="mt-1 list-disc pl-5 text-brix-muted">
                        {passResult.skipped.map((s) => (
                          <li key={s.job}>
                            Job {s.job}: {s.reason}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </section>
          </>
        )
      )}

      <DataTable
        columns={columns}
        rows={q.data?.rows}
        rowKey={(r) => r.id}
        isLoading={q.isLoading}
        error={q.error ? (q.error as Error).message : null}
        empty="No review emails yet."
        initialSort={{ key: 'created', dir: 'desc' }}
        caption="Review requests"
      />
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-3">
      <div className="text-xs text-brix-muted">{label}</div>
      <div className="text-lg font-semibold text-brix-ink tabular-nums">{value}</div>
    </div>
  );
}
